import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  MdSearch, MdAdd, MdEdit, MdDelete, MdRefresh,
  MdCategory, MdSubdirectoryArrowRight,
  MdToggleOn, MdToggleOff, MdHistory, MdUploadFile,
  MdBadge, MdPerson, MdEmail, MdPhone
} from 'react-icons/md';
import SkeletonRow from '../components/SkeletonRow';
import { helpdeskCategoryApi, helpdeskSubCategoryApi, idCardHistoryApi } from '../../../api/helpdeskApi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './HelpdeskMasterData.css';

const TABS = [
  { id: 'categories', label: 'Categories', icon: MdCategory },
  { id: 'subCategories', label: 'Sub Categories', icon: MdSubdirectoryArrowRight },
  { id: 'idCardHistory', label: 'ID Card History', icon: MdHistory }
];

function HelpdeskMasterData() {
  const [activeTab, setActiveTab] = useState('categories');
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [categories, setCategories] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({});

  // ID Card History state
  const [idCardSearchInput, setIdCardSearchInput] = useState('');
  const [idCardResults, setIdCardResults] = useState([]);
  const [idCardLoading, setIdCardLoading] = useState(false);
  const [idCardTotalCount, setIdCardTotalCount] = useState(null);
  const [idCardImporting, setIdCardImporting] = useState(false);
  const [idCardSelectedRecord, setIdCardSelectedRecord] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [idEntriesFile, setIdEntriesFile] = useState(null);
  const [usersFile, setUsersFile] = useState(null);
  const idEntriesInputRef = useRef(null);
  const usersFileInputRef = useRef(null);

  const loadCategories = async () => {
    try {
      const cats = await helpdeskCategoryApi.getAll();
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (e) {
      console.error('Failed to load categories:', e);
    }
  };

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      let result;
      if (activeTab === 'categories') {
        result = await helpdeskCategoryApi.getAll();
      } else {
        result = await helpdeskSubCategoryApi.getAll();
      }
      setData(Array.isArray(result) ? result : []);
    } catch (e) {
      console.error(`Failed to load ${activeTab}:`, e);
      toast.error(`Failed to load ${activeTab}`);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'idCardHistory') {
      loadData();
    }
    if (activeTab === 'subCategories') {
      loadCategories();
    }
    if (activeTab === 'idCardHistory' && idCardTotalCount === null) {
      idCardHistoryApi.getCount().then(d => setIdCardTotalCount(d.total)).catch(() => {});
    }
  }, [activeTab, loadData]);

  const handleIdCardSearch = async () => {
    const q = idCardSearchInput.trim();
    if (!q) { toast.warning('Enter an email, name, or employee ID to search'); return; }
    setIdCardLoading(true);
    try {
      let results;
      if (q.includes('@')) {
        results = await idCardHistoryApi.getByEmail(q);
      } else {
        results = await idCardHistoryApi.search(q);
      }
      setIdCardResults(Array.isArray(results) ? results : []);
      if (!results || results.length === 0) toast.info('No ID card records found');
    } catch (e) {
      toast.error('Search failed');
    } finally {
      setIdCardLoading(false);
    }
  };

  const handleImportSubmit = async () => {
    if (!idEntriesFile) { toast.warning('Please select the ID Entries CSV file'); return; }
    if (!usersFile) { toast.warning('Please select the Users (mas_users) CSV file'); return; }
    setIdCardImporting(true);
    try {
      const result = await idCardHistoryApi.importTwoFiles(idEntriesFile, usersFile);
      const d = result.data || {};
      toast.success(`Import complete — ${d.inserted || 0} inserted, ${d.skipped || 0} skipped, ${d.noMatch || 0} unmatched`);
      if (d.errors > 0) toast.warning(`${d.errors} rows had errors`);
      setShowImportModal(false);
      setIdEntriesFile(null);
      setUsersFile(null);
      idCardHistoryApi.getCount().then(cnt => setIdCardTotalCount(cnt.total)).catch(() => {});
    } catch (e) {
      const msg = e.response?.data?.message || 'Import failed';
      toast.error(msg);
    } finally {
      setIdCardImporting(false);
      if (idEntriesInputRef.current) idEntriesInputRef.current.value = '';
      if (usersFileInputRef.current) usersFileInputRef.current.value = '';
    }
  };

  const getFormFields = () => {
    if (activeTab === 'categories') {
      return [
        { name: 'categoryCode', label: 'Category Code', required: true },
        { name: 'categoryName', label: 'Category Name', required: true },
        { name: 'description', label: 'Description', type: 'textarea' },
        { name: 'displayOrder', label: 'Display Order', type: 'number' }
      ];
    }
    return [
      { name: 'subCategoryCode', label: 'Sub Category Code', required: true },
      { name: 'subCategoryName', label: 'Sub Category Name', required: true },
      { name: 'categoryId', label: 'Parent Category', type: 'select', required: true,
        options: categories.map(c => ({ value: c.id, label: c.categoryName })) },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'displayOrder', label: 'Display Order', type: 'number' }
    ];
  };

  const getTableColumns = () => {
    if (activeTab === 'categories') {
      return ['Code', 'Name', 'Description', 'Status', 'Order', 'Actions'];
    }
    return ['Code', 'Name', 'Category', 'Description', 'Status', 'Order', 'Actions'];
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    if (activeTab === 'categories') {
      setFormData({
        categoryCode: item.categoryCode,
        categoryName: item.categoryName,
        description: item.description || '',
        displayOrder: item.displayOrder || ''
      });
    } else {
      setFormData({
        subCategoryCode: item.subCategoryCode,
        subCategoryName: item.subCategoryName,
        categoryId: item.category?.id || '',
        description: item.description || '',
        displayOrder: item.displayOrder || ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (activeTab === 'categories') {
        if (editingItem) {
          await helpdeskCategoryApi.update(editingItem.id, formData);
          toast.success('Category updated successfully');
        } else {
          await helpdeskCategoryApi.create(formData);
          toast.success('Category created successfully');
        }
      } else {
        const { categoryId, ...subCatData } = formData;
        if (editingItem) {
          await helpdeskSubCategoryApi.update(editingItem.id, subCatData, categoryId);
          toast.success('Sub-category updated successfully');
        } else {
          await helpdeskSubCategoryApi.create(subCatData, categoryId);
          toast.success('Sub-category created successfully');
        }
      }
      setShowModal(false);
      loadData();
      if (activeTab === 'categories') loadCategories();
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Operation failed';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (item) => {
    try {
      const newStatus = item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      if (activeTab === 'categories') {
        await helpdeskCategoryApi.updateStatus(item.id, newStatus);
      } else {
        await helpdeskSubCategoryApi.updateStatus(item.id, newStatus);
      }
      toast.success(`Status changed to ${newStatus}`);
      loadData();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (item) => {
    try {
      if (activeTab === 'categories') {
        await helpdeskCategoryApi.delete(item.id);
      } else {
        await helpdeskSubCategoryApi.delete(item.id);
      }
      toast.success('Deleted successfully');
      setDeleteConfirm(null);
      loadData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const filteredData = data.filter(item => {
    const q = searchQuery.toLowerCase();
    if (activeTab === 'categories') {
      return (item.categoryCode?.toLowerCase().includes(q) || item.categoryName?.toLowerCase().includes(q));
    }
    return (item.subCategoryCode?.toLowerCase().includes(q) || item.subCategoryName?.toLowerCase().includes(q));
  });

  const renderTableRow = (item, idx) => {
    const statusBadge = (
      <span className={`status-badge ${item.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
        {item.status}
      </span>
    );
    const actions = (
      <div className="action-buttons">
        <button className="btn-icon btn-edit" onClick={() => handleEdit(item)} title="Edit"><MdEdit /></button>
        <button
          className={`btn-icon ${item.status === 'ACTIVE' ? 'btn-deactivate' : 'btn-activate'}`}
          onClick={() => handleToggleStatus(item)}
          title={item.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
        >
          {item.status === 'ACTIVE' ? <MdToggleOn /> : <MdToggleOff />}
        </button>
        <button className="btn-icon btn-delete" onClick={() => setDeleteConfirm(item)} title="Delete"><MdDelete /></button>
      </div>
    );

    if (activeTab === 'categories') {
      return (
        <tr key={item.id}>
          <td>{idx + 1}</td>
          <td>{item.categoryCode}</td>
          <td>{item.categoryName}</td>
          <td>{item.description || '-'}</td>
          <td>{statusBadge}</td>
          <td>{item.displayOrder || '-'}</td>
          <td>{actions}</td>
        </tr>
      );
    }
    return (
      <tr key={item.id}>
        <td>{idx + 1}</td>
        <td>{item.subCategoryCode}</td>
        <td>{item.subCategoryName}</td>
        <td>{item.category?.categoryName || '-'}</td>
        <td>{item.description || '-'}</td>
        <td>{statusBadge}</td>
        <td>{item.displayOrder || '-'}</td>
        <td>{actions}</td>
      </tr>
    );
  };

  const statusLabel = (s) => {
    const map = {
      // Legacy values (existing DB records)
      DRAFT: 'Draft', SUBMITTED: 'Submitted', PROCESSING: 'Processing', PRINTED: 'Printed', COMPLETED: 'Completed',
      // New values
      REGISTERED: 'Registered',
      SENT_FOR_PRINT: 'Sent for Print',
      RECEIVED_FROM_VENDOR: 'Received from Vendor',
      DELIVERED: 'Delivered to User',
      YET_TO_REGISTER: 'Yet to Register',
      UNKNOWN: 'Unknown'
    };
    return map[s] || s;
  };
  const statusClass = (s) => {
    const map = {
      // Legacy values
      DRAFT: 'draft', SUBMITTED: 'open', PROCESSING: 'in-progress', PRINTED: 'assigned', COMPLETED: 'resolved',
      // New values
      REGISTERED: 'open',
      SENT_FOR_PRINT: 'in-progress',
      RECEIVED_FROM_VENDOR: 'assigned',
      DELIVERED: 'resolved',
      YET_TO_REGISTER: 'draft',
      UNKNOWN: 'draft'
    };
    return map[s] || 'draft';
  };

  return (
    <div className="helpdesk-master-data">
      <div className="page-header">
        <h2>Helpdesk Master Data</h2>
      </div>

      <div className="tabs-container">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab.id); setSearchQuery(''); setIdCardResults([]); setIdCardSearchInput(''); setIdCardSelectedRecord(null); }}
          >
            <tab.icon /> {tab.label}
          </button>
        ))}
      </div>

      {/* ===== ID CARD HISTORY TAB ===== */}
      {activeTab === 'idCardHistory' ? (
        <div className="id-card-history-tab">
          <div className="id-card-history-header">
            <div className="id-card-stats">
              <MdHistory className="id-card-stats-icon" />
              <span>Legacy ID Card Records</span>
              {idCardTotalCount !== null && (
                <span className="id-card-count-badge">{idCardTotalCount.toLocaleString()} imported</span>
              )}
            </div>
            <div className="id-card-import-section">
              <button
                className="btn btn-secondary"
                onClick={() => setShowImportModal(true)}
                title="Upload id_entries.csv + mas_users.csv to import"
              >
                <MdUploadFile /> Import from Old System
              </button>
            </div>
          </div>

          <div className="id-card-search-bar">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by email, name, or employee ID..."
                value={idCardSearchInput}
                onChange={e => setIdCardSearchInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleIdCardSearch()}
              />
            </div>
            <button className="btn btn-primary" onClick={handleIdCardSearch} disabled={idCardLoading}>
              {idCardLoading ? 'Searching...' : 'Search'}
            </button>
          </div>

          {idCardResults.length > 0 && (
            <div className="id-card-results">
              <p className="results-count">{idCardResults.length} record(s) found</p>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Employee ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Blood Group</th>
                      <th>Contact</th>
                      <th>Legacy Status</th>
                      <th>ID Card Status</th>
                      <th>Update Status</th>
                      <th>Submitted On</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {idCardResults.map((rec, idx) => (
                      <tr key={rec.id}>
                        <td>{idx + 1}</td>
                        <td>{rec.employeeId || '-'}</td>
                        <td>{rec.employeeName || '-'}</td>
                        <td>{rec.employeeEmail || '-'}</td>
                        <td>{rec.bloodGroup || '-'}</td>
                        <td>{rec.contact || '-'}</td>
                        <td>
                          <span className={`status-badge ${statusClass(rec.status)}`}>
                            {statusLabel(rec.status)}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${rec.idCardStatus === 'DELIVERED' ? 'resolved' : rec.idCardStatus === 'PRINT_READY' ? 'assigned' : rec.idCardStatus === 'PRINT_QUEUE' ? 'in-progress' : 'open'}`}>
                            {rec.idCardStatus ? rec.idCardStatus.replace(/_/g, ' ') : '-'}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${rec.updateStatus === 'CLOSED' ? 'resolved' : rec.updateStatus === 'IN_PROGRESS' ? 'in-progress' : 'open'}`}>
                            {rec.updateStatus ? rec.updateStatus.replace(/_/g, ' ') : '-'}
                          </span>
                        </td>
                        <td>{rec.legacyCreatedAt ? new Date(rec.legacyCreatedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                        <td>
                          <button className="btn-icon btn-view" onClick={() => setIdCardSelectedRecord(rec)} title="View Details">
                            <MdBadge />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {idCardResults.length === 0 && !idCardLoading && (
            <div className="id-card-empty-state">
              <MdBadge className="empty-icon" />
              <p>Search for an employee's ID card request history by entering their email address, name, or employee ID above.</p>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="toolbar">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder={`Search ${activeTab === 'categories' ? 'categories' : 'sub-categories'}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="toolbar-actions">
              <button className="btn btn-secondary" onClick={loadData}><MdRefresh /> Refresh</button>
              <button className="btn btn-primary" onClick={handleAdd}><MdAdd /> Add New</button>
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  {getTableColumns().map((col, i) => <th key={i}>{col}</th>)}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array(5).fill(null).map((_, i) => <SkeletonRow key={i} cols={getTableColumns().length + 1} />)
                ) : filteredData.length === 0 ? (
                  <tr><td colSpan={getTableColumns().length + 1} className="empty-message">No data found</td></tr>
                ) : (
                  filteredData.map((item, idx) => renderTableRow(item, idx))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>{editingItem ? 'Edit' : 'Add'} {activeTab === 'categories' ? 'Category' : 'Sub Category'}</h3>
            <form onSubmit={handleSubmit}>
              {getFormFields().map(field => (
                <div className="form-group" key={field.name}>
                  <label>{field.label} {field.required && <span className="required">*</span>}</label>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={formData[field.name] || ''}
                      onChange={e => setFormData({ ...formData, [field.name]: e.target.value })}
                      rows={3}
                    />
                  ) : field.type === 'select' ? (
                    <select
                      value={formData[field.name] || ''}
                      onChange={e => setFormData({ ...formData, [field.name]: e.target.value })}
                      required={field.required}
                    >
                      <option value="">Select...</option>
                      {field.options?.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type || 'text'}
                      value={formData[field.name] || ''}
                      onChange={e => setFormData({ ...formData, [field.name]: e.target.value })}
                      required={field.required}
                    />
                  )}
                </div>
              ))}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ID Card Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => !idCardImporting && setShowImportModal(false)}>
          <div className="modal-content modal-import" onClick={e => e.stopPropagation()}>
            <h3><MdUploadFile /> Import ID Card Records from Old System</h3>
            <p className="import-modal-desc">
              Upload the two raw export files from the old system. The backend will join them on <code>vfs_id</code> and import only matched records.
            </p>

            <div className="import-file-steps">
              {/* Step 1 */}
              <div className={`import-step ${idEntriesFile ? 'step-done' : ''}`}>
                <div className="step-number">1</div>
                <div className="step-content">
                  <div className="step-label">ID Card Entries File</div>
                  <div className="step-hint">The <strong>id_entries.csv</strong> export from the old system</div>
                  {idEntriesFile ? (
                    <div className="step-file-selected">
                      <span className="file-name">{idEntriesFile.name}</span>
                      <button className="btn-clear" onClick={() => { setIdEntriesFile(null); if(idEntriesInputRef.current) idEntriesInputRef.current.value=''; }}>✕</button>
                    </div>
                  ) : (
                    <button className="btn btn-outline" onClick={() => idEntriesInputRef.current?.click()}>
                      Choose id_entries.csv
                    </button>
                  )}
                  <input ref={idEntriesInputRef} type="file" accept=".csv" style={{ display: 'none' }}
                    onChange={e => setIdEntriesFile(e.target.files?.[0] || null)} />
                </div>
              </div>

              <div className="import-step-arrow">↓</div>

              {/* Step 2 */}
              <div className={`import-step ${usersFile ? 'step-done' : ''}`}>
                <div className="step-number">2</div>
                <div className="step-content">
                  <div className="step-label">Employee Master File</div>
                  <div className="step-hint">The <strong>mas_users.csv</strong> export — used to resolve employee emails</div>
                  {usersFile ? (
                    <div className="step-file-selected">
                      <span className="file-name">{usersFile.name}</span>
                      <button className="btn-clear" onClick={() => { setUsersFile(null); if(usersFileInputRef.current) usersFileInputRef.current.value=''; }}>✕</button>
                    </div>
                  ) : (
                    <button className="btn btn-outline" onClick={() => usersFileInputRef.current?.click()}>
                      Choose mas_users.csv
                    </button>
                  )}
                  <input ref={usersFileInputRef} type="file" accept=".csv" style={{ display: 'none' }}
                    onChange={e => setUsersFile(e.target.files?.[0] || null)} />
                </div>
              </div>
            </div>

            <div className="import-modal-note">
              <strong>Note:</strong> Re-importing is safe — already imported records will be skipped automatically (idempotent).
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowImportModal(false)} disabled={idCardImporting}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleImportSubmit}
                disabled={idCardImporting || !idEntriesFile || !usersFile}
              >
                {idCardImporting ? 'Importing...' : 'Import Records'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ID Card Detail Modal */}
      {idCardSelectedRecord && (
        <div className="modal-overlay" onClick={() => setIdCardSelectedRecord(null)}>
          <div className="modal-content modal-id-card" onClick={e => e.stopPropagation()}>
            <h3><MdBadge /> ID Card Request — Legacy Record</h3>
            <div className="id-card-detail-grid">
              <div className="id-card-detail-row">
                <span className="detail-label"><MdPerson /> Employee</span>
                <span className="detail-value">{idCardSelectedRecord.employeeName} ({idCardSelectedRecord.employeeId})</span>
              </div>
              <div className="id-card-detail-row">
                <span className="detail-label"><MdEmail /> Email</span>
                <span className="detail-value">{idCardSelectedRecord.employeeEmail}</span>
              </div>
              <div className="id-card-detail-row">
                <span className="detail-label">Legacy Status</span>
                <span className="detail-value">
                  <span className={`status-badge ${statusClass(idCardSelectedRecord.status)}`}>
                    {statusLabel(idCardSelectedRecord.status)}
                  </span>
                </span>
              </div>
              <div className="id-card-detail-row">
                <span className="detail-label">ID Card Status</span>
                <span className="detail-value">
                  <span className={`status-badge ${idCardSelectedRecord.idCardStatus === 'DELIVERED' ? 'resolved' : idCardSelectedRecord.idCardStatus === 'PRINT_READY' ? 'assigned' : idCardSelectedRecord.idCardStatus === 'PRINT_QUEUE' ? 'in-progress' : 'open'}`}>
                    {idCardSelectedRecord.idCardStatus ? idCardSelectedRecord.idCardStatus.replace(/_/g, ' ') : '-'}
                  </span>
                </span>
              </div>
              <div className="id-card-detail-row">
                <span className="detail-label">Update Status</span>
                <span className="detail-value">
                  <span className={`status-badge ${idCardSelectedRecord.updateStatus === 'CLOSED' ? 'resolved' : idCardSelectedRecord.updateStatus === 'IN_PROGRESS' ? 'in-progress' : 'open'}`}>
                    {idCardSelectedRecord.updateStatus ? idCardSelectedRecord.updateStatus.replace(/_/g, ' ') : '-'}
                  </span>
                </span>
              </div>
              <div className="id-card-detail-row">
                <span className="detail-label">Blood Group</span>
                <span className="detail-value">{idCardSelectedRecord.bloodGroup || '-'}</span>
              </div>
              <div className="id-card-detail-row">
                <span className="detail-label"><MdPhone /> Contact</span>
                <span className="detail-value">{idCardSelectedRecord.contact || '-'}</span>
              </div>
              <div className="id-card-detail-row">
                <span className="detail-label">Emergency Contact</span>
                <span className="detail-value">{idCardSelectedRecord.emergencyContact || '-'}</span>
              </div>
              <div className="id-card-detail-row full-width">
                <span className="detail-label">Address</span>
                <span className="detail-value">{idCardSelectedRecord.address || '-'}</span>
              </div>
              {idCardSelectedRecord.imageName && (
                <div className="id-card-detail-row">
                  <span className="detail-label">Photo File</span>
                  <span className="detail-value detail-filename">{idCardSelectedRecord.imageName}</span>
                </div>
              )}
              <div className="id-card-detail-row">
                <span className="detail-label">Submitted On</span>
                <span className="detail-value">
                  {idCardSelectedRecord.legacyCreatedAt
                    ? new Date(idCardSelectedRecord.legacyCreatedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                    : '-'}
                </span>
              </div>
              <div className="id-card-detail-row">
                <span className="detail-label">Last Updated</span>
                <span className="detail-value">
                  {idCardSelectedRecord.legacyUpdatedAt
                    ? new Date(idCardSelectedRecord.legacyUpdatedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                    : '-'}
                </span>
              </div>
              {idCardSelectedRecord.reprocessReason && (
                <div className="id-card-detail-row full-width">
                  <span className="detail-label">Reprocess Reason</span>
                  <span className="detail-value">{idCardSelectedRecord.reprocessReason}</span>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setIdCardSelectedRecord(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirm.categoryName || deleteConfirm.subCategoryName}</strong>? This will deactivate the item. You can reactivate it later using the toggle button.</p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HelpdeskMasterData;
